import React from 'react';

interface CitationChipProps {
  /** Display name (usually domain) */
  displayName: string;
  /** Full URL to link to */
  url: string;
  /** Citation number for accessibility */
  index: number;
  /** Optional: compact style for inline use */
  compact?: boolean;
}

/**
 * CitationChip - A clean, clickable chip for source citations
 *
 * Styled similar to ChatGPT's citation badges with a subtle, professional look.
 * Opens links in new tab with proper security attributes.
 */
export const CitationChip: React.FC<CitationChipProps> = ({
  displayName,
  url,
  index,
  compact = false
}) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      aria-label={`Source ${index}: ${displayName}`}
      className={`
        inline-flex items-center gap-1.5
        ${compact ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm'}
        bg-white/10 hover:bg-white/20
        border border-white/20 hover:border-white/30
        rounded-full
        text-white/80 hover:text-white
        transition-all duration-150
        cursor-pointer
        no-underline
        font-medium
      `}
    >
      <span className="text-white/60 text-xs">{index}</span>
      <span className="truncate max-w-[120px]">{displayName}</span>
    </a>
  );
};

/**
 * Light theme variant of CitationChip for use on light backgrounds
 */
export const CitationChipLight: React.FC<CitationChipProps> = ({
  displayName,
  url,
  index,
  compact = false
}) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      aria-label={`Source ${index}: ${displayName}`}
      className={`
        inline-flex items-center gap-1.5
        ${compact ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm'}
        bg-gray-100 hover:bg-gray-200
        border border-gray-200 hover:border-gray-300
        rounded-full
        text-gray-700 hover:text-gray-900
        transition-all duration-150
        cursor-pointer
        no-underline
        font-medium
      `}
    >
      <span className="text-gray-500 text-xs">{index}</span>
      <span className="truncate max-w-[120px]">{displayName}</span>
    </a>
  );
};

export default CitationChip;
